import {
	makeDraggable,
	makeDroppable,
	registerNonDroppable,
} from "../../../../dragAndDrop/dragAndDropSupport.js";
import AbstractDecorator from "./AbstractDecorator.js";

/**
 * Wrapper strategy adding drag and drop support to any strategy.
 *
 * @param {AbstractListStrategy} strategy - Original strategy to wrap
 * @param {function} [isDraggable] - Function invoked with an item and its index.
 *   When returning `true` it adds draggable capability to the item corresponding tagBrush
 * @param {function} [isDroppable] - Function invoked with an item and its index.
 *   When returning `true` it adds droppable capability to the item corresponding tagBrush
 *   This function is invoked with the list of sortable items, in the order chosen by the user
 * @param {function} [getDragHandle] - When provided, narrow down the drag
 *   handle to the result of the function. This function is invoked with
 *   the Card DOMElement
 */
class DragAndDropStrategyDecorator extends AbstractDecorator {
	constructor({
		isDraggable = () => false,
		isDroppable = () => false,
		onChange = () => {},
		getDragHandle,
	}) {
		super(...arguments);

		this._isDraggable = isDraggable;
		this._isDroppable = isDroppable;
		this._onChange = onChange;
		this._getDragHandle = getDragHandle;
	}

	makeItemRenderable({ html, item, index }) {
		const builtItem = this._strategy.makeItemRenderable({
			html,
			item,
			index,
		});

		if (this._isDraggable({ item, index })) {
			makeDraggable(builtItem, {
				getDragHandle: this._getDragHandle,
			});
		}

		if (this._isDroppable({ item, index })) {
			makeDroppable(builtItem, {
				item,
				container: this,
				onChange: this._onChange.bind(this),
			});
		} else {
			registerNonDroppable({
				tagBrush: builtItem,
				item,
				container: this,
			});
		}

		return builtItem;
	}
}

/**
 * Decorate a strategy with drag and drop capabilities.
 *
 * @param {AbstractListStrategy} strategy - Original strategy to wrap
 * @param {object} options - Object parameters
 * @param {function} [options.isDraggable] - When true, adds draggable
 *   capability
 * @param {function} [options.isDroppable] - When true, adds droppable
 *   capability
 * @param {function} [options.onChange] - Function invoked when the user
 *   reorders the items
 * @param {function} [options.getDragHandle] - When provided, narrow down
 *   the drag handle to the result of the function
 * @return {AbstractListStrategy}
 */
export default function withDragAndDrop(
	strategy,
	{ isDraggable, isDroppable, onChange, getDragHandle },
) {
	return new DragAndDropStrategyDecorator({
		strategy,
		isDraggable,
		isDroppable,
		onChange,
		getDragHandle,
	});
}
